import { useEffect, useRef } from "react";
import { cn } from "@/shadcn/lib/utils";
import { ReactComponent as XIcon } from "atoms/icons/close.svg";
import type { Tag, TagItemWithFocusProps, TagListProps } from "./types";

const TagItem = <T extends Tag = Tag>({
  tag,
  shouldFocus,
  "data-testid": dataTestId,
  disabled,
  onClose,
}: TagItemWithFocusProps<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (shouldFocus && buttonRef.current) buttonRef.current.focus();
  }, [shouldFocus]);
  const handleClose = () => {
    if (!disabled) onClose(tag);
  };
  return (
    <div
      className="inline-flex max-w-full min-w-0 shrink-0 items-center gap-2.5 rounded-lg border px-4 py-2 type-body-sm"
      data-testid={dataTestId}
    >
      <span
        className="type-body-sm leading-[140%] font-normal whitespace-nowrap text-slate-900"
        title={tag.label}
      >
        {tag.label}
      </span>
      <button
        aria-label={`Remove ${tag.label} tag`}
        className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-hidden"
        disabled={disabled}
        ref={buttonRef}
        type="button"
        onClick={handleClose}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClose();
          }
        }}
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export const TagList = <T extends Tag = Tag>({
  tags,
  tagComponent: CustomTagComponent,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
  disabled,
  onTagRemove,
}: TagListProps<T>) => {
  const focusTagIdRef = useRef<string | null>(null);
  const TagComponent = CustomTagComponent || TagItem;
  if (tags.length === 0) return null;
  const handleTagRemove = (tag: T) => {
    const currentIndex = tags.findIndex((item) => item.id === tag.id);
    focusTagIdRef.current = tags[currentIndex + 1]?.id ?? tags[currentIndex - 1]?.id ?? null;
    onTagRemove(tag);
  };
  return (
    <div
      aria-label={ariaLabel || "Tag list"}
      className={cn("flex flex-wrap gap-2", className)}
      data-testid={dataTestId}
      role="group"
    >
      {tags.map((tag) => (
        <TagComponent
          data-testid={dataTestId ? `${dataTestId}-tag-${tag.id}` : undefined}
          disabled={disabled}
          key={tag.id}
          shouldFocus={tag.id === focusTagIdRef.current}
          tag={tag}
          onClose={handleTagRemove}
        />
      ))}
    </div>
  );
};
